import { Link, useNavigate } from '@tanstack/react-router';
import { CreditCard, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { ApiError, isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useCompleteStripeConnect } from '../hooks/use-stripe-mutations';
import { isProviderRejected } from '../schemas/stripe-rules';
import {
  stripePublishableKeySchema,
  type StripePublishableKeyValues,
} from '../schemas/stripe-schemas';

export interface StripeCallbackPageProps {
  storeId: string;
  code: string | undefined;
  state: string | undefined;
  /** Stripe sends `error` (e.g. access_denied) instead of a code when the owner backs out. */
  error: string | undefined;
}

/** A bad or expired state comes back as a 422 on `body.state`: the whole connect must restart. */
function isStateRejected(error: unknown): boolean {
  return error instanceof ApiError && error.fieldErrors['body.state'] !== undefined;
}

/** Stripe's token response carried no publishable key, so the backend asks for it (400). */
function isPublishableKeyMissing(error: unknown): boolean {
  return error instanceof ApiError && error.fieldErrors['body.publishableKey'] !== undefined;
}

/**
 * Where Stripe returns the owner after Connect. The code is exchanged as soon as the page opens,
 * exactly once (the ref survives StrictMode's second effect run); the backend takes the
 * publishable key from Stripe's token response. Only when Stripe sent none does the page ask
 * the owner to paste it and exchange again with the key.
 */
export function StripeCallbackPage({ storeId, code, state, error }: StripeCallbackPageProps) {
  const { t } = useTranslation(['paymentMethods', 'errors']);
  const navigate = useNavigate();
  const complete = useCompleteStripeConnect(storeId);
  const message = useErrorMessage();
  const form = useForm<StripePublishableKeyValues>({
    resolver: useI18nResolver(stripePublishableKeySchema),
    defaultValues: { publishableKey: '' },
  });
  const { mutateAsync } = complete;
  const started = useRef(false);
  const [needsKey, setNeedsKey] = useState(false);
  const restart = isStateRejected(complete.error) || isProviderRejected(complete.error);
  const serverError = form.formState.errors.root?.server?.message;
  const requestId = isApiError(complete.error) ? complete.error.requestId : undefined;

  const back = (
    <Button asChild variant="outline">
      <Link to="/s/$storeId/settings/payment-methods" params={{ storeId }}>
        {t('paymentMethods:stripe.callback.back')}
      </Link>
    </Button>
  );

  useEffect(() => {
    if (error || !code || !state || started.current) return;
    started.current = true;
    const exchange = async () => {
      try {
        await mutateAsync({ code, state });
      } catch (failure) {
        if (isPublishableKeyMissing(failure)) setNeedsKey(true);
        return;
      }
      await navigate({ to: '/s/$storeId/settings/payment-methods', params: { storeId } });
    };
    void exchange();
  }, [code, state, error, mutateAsync, navigate, storeId]);

  const submit = form.handleSubmit(async (values) => {
    if (!code || !state) return;
    try {
      await complete.mutateAsync({ code, state, publishableKey: values.publishableKey });
      await navigate({ to: '/s/$storeId/settings/payment-methods', params: { storeId } });
    } catch (failure) {
      if (isStateRejected(failure) || isProviderRejected(failure)) return;
      if (applyFieldErrors(failure, form.setError, { knownFields: new Set(['publishableKey']) })) {
        return;
      }
      form.setError('root.server', { type: 'server', message: message(failure) });
    }
  });

  let body: ReactNode;
  if (error) {
    body = (
      <div className="flex flex-col gap-4">
        <Alert role="alert">
          <AlertTitle>{t('paymentMethods:stripe.callback.cancelledTitle')}</AlertTitle>
          <AlertDescription>{t('paymentMethods:stripe.callback.cancelled')}</AlertDescription>
        </Alert>
        <div>{back}</div>
      </div>
    );
  } else if (!code || !state) {
    body = (
      <div className="flex flex-col gap-4">
        <p className="text-muted-foreground text-sm">
          {t('paymentMethods:stripe.callback.missing')}
        </p>
        <div>{back}</div>
      </div>
    );
  } else if (restart) {
    body = (
      <div className="flex flex-col gap-4">
        <Alert variant="destructive" role="alert">
          <AlertTitle>{t('paymentMethods:stripe.callback.restartTitle')}</AlertTitle>
          <AlertDescription>
            {isProviderRejected(complete.error)
              ? t('paymentMethods:stripe.callback.rejected')
              : t('paymentMethods:stripe.callback.expired')}
            {requestId ? (
              <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
            ) : null}
          </AlertDescription>
        </Alert>
        <div>{back}</div>
      </div>
    );
  } else if (!needsKey) {
    body = complete.isError ? (
      <div className="flex flex-col gap-4">
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {message(complete.error)}
            {requestId ? (
              <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
            ) : null}
          </AlertDescription>
        </Alert>
        <div>{back}</div>
      </div>
    ) : (
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {t('paymentMethods:stripe.callback.connecting')}
      </p>
    );
  } else {
    body = (
      <Form {...form}>
        <form onSubmit={submit} noValidate className="flex max-w-lg flex-col gap-4">
          {serverError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                {serverError}
                {requestId ? (
                  <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
          <FormField
            control={form.control}
            name="publishableKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('paymentMethods:stripe.keys.publishableKey')}</FormLabel>
                <FormControl>
                  <Input
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    className="font-mono text-[13px]"
                    placeholder="pk_live_…"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t('paymentMethods:stripe.callback.publishableKeyHint')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={complete.isPending}>
              {complete.isPending
                ? t('paymentMethods:stripe.callback.submitting')
                : t('paymentMethods:stripe.callback.submit')}
            </Button>
            {back}
          </div>
        </form>
      </Form>
    );
  }

  return (
    <PageContent>
      <PageHeader title={t('paymentMethods:stripe.callback.title')} />
      <Card className="max-w-2xl gap-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="size-4" aria-hidden />
            {t('paymentMethods:stripe.title')}
          </CardTitle>
          <CardDescription>
            {needsKey
              ? t('paymentMethods:stripe.callback.needsKey')
              : t('paymentMethods:stripe.callback.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>{body}</CardContent>
      </Card>
    </PageContent>
  );
}
